import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Card,
  Autocomplete, FormControlLabel, Switch, Tabs, Tab, Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as PrescriptionIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import type { EvolutionAttachedPrintable } from '../evolution/evolutionWorkflowTypes';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const typeLabels: Record<string, string> = {
  simple: 'Simples', controlled_blue: 'Controlado (Azul)', controlled_yellow: 'Controlado (Amarelo)',
  special_white: 'Especial (Branco)', antimicrobial: 'Antimicrobiano', dental: 'Odontológico',
};

const routeOptions = ['Oral', 'Sublingual', 'Tópica', 'Intramuscular', 'Intravenosa', 'Subcutânea', 'Retal', 'Nasal', 'Inalatória', 'Oftálmica', 'Otológica', 'Vaginal'];
const formOptions = ['Comprimido', 'Cápsula', 'Drágea', 'Solução Oral', 'Suspensão', 'Xarope', 'Gotas', 'Pomada', 'Creme', 'Gel', 'Solução Injetável', 'Supositório', 'Aerossol', 'Spray', 'Colírio', 'Sachê', 'Pó'];

const emptyItem = {
  medication_name: '', active_ingredient: '', concentration: '', pharmaceutical_form: '',
  dosage: '', frequency: '', route: 'Oral', duration: '', quantity: '',
  instructions: '', special_instructions: '', is_controlled: false, control_class: '',
  is_antimicrobial: false, is_continuous_use: false, sort_order: 0,
};

function summaryFromPrescription(pr: any, fallbackItems: any[]): string {
  const items = pr?.items?.length ? pr.items : fallbackItems;
  const lines: string[] = [];
  if (pr?.prescription_number) lines.push(`Receituário nº ${pr.prescription_number}`);
  items.filter((i: any) => i?.medication_name?.trim()).forEach((i: any, idx: number) => {
    lines.push(`${idx + 1}. ${i.medication_name} — ${i.dosage || ''}, ${i.frequency || ''}`);
  });
  if (pr?.notes) lines.push(`Obs.: ${pr.notes}`);
  return lines.join('\n') || 'Receituário registrado.';
}

export interface PrescriptionFormDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
  /** Template receituário (API) aplicado ao abrir */
  prefillTemplate?: any | null;
  onSuccess: (item: EvolutionAttachedPrintable) => void;
}

const PrescriptionFormDialog: React.FC<PrescriptionFormDialogProps> = ({
  open,
  onClose,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
  prefillTemplate,
  onSuccess,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [professionalSearch, setProfessionalSearch] = useState('');
  const [professionalOptions, setProfessionalOptions] = useState<any[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  const [items, setItems] = useState<any[]>([{ ...emptyItem }]);
  const [prescriptionForm, setPrescriptionForm] = useState({
    prescription_type: 'simple',
    diagnosis: '', diagnosis_code: '', clinical_indication: '',
    notes: '', internal_notes: '', is_continuous_use: false, renewal_date: '',
  });

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions/templates'), { params: { is_active: true } });
        setTemplates(res.data?.data || []);
      } catch {
        setTemplates([]);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSelectedPatient(initialPatient?.id ? initialPatient : (patientId ? { id: patientId, full_name: '…' } : null));
    setSelectedProfessional(initialProfessional?.id ? initialProfessional : (professionalId ? { id: professionalId, full_name: '…' } : null));
    setPatientOptions(initialPatient ? [initialPatient] : []);
    setProfessionalOptions(initialProfessional ? [initialProfessional] : []);
    setPatientSearch('');
    setProfessionalSearch('');
    setTabValue(0);
    setPrescriptionForm({
      prescription_type: 'simple', diagnosis: '', diagnosis_code: '', clinical_indication: '',
      notes: '', internal_notes: '', is_continuous_use: false, renewal_date: '',
    });
    setItems([{ ...emptyItem }]);
    if (prefillTemplate) {
      setPrescriptionForm((prev: any) => ({
        ...prev,
        prescription_type: prefillTemplate.prescription_type || 'simple',
        diagnosis: prefillTemplate.default_diagnosis || '',
        diagnosis_code: prefillTemplate.default_diagnosis_code || '',
        notes: prefillTemplate.default_notes || '',
      }));
      const tplItems = (prefillTemplate.items || []).map((it: any, idx: number) => ({
        ...emptyItem,
        ...it,
        id: undefined,
        sort_order: idx,
      }));
      setItems(tplItems.length > 0 ? tplItems : [{ ...emptyItem }]);
    }
  }, [open, patientId, professionalId, initialPatient, initialProfessional, prefillTemplate]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions-search/patients'), { params: { search: patientSearch || '' } });
        setPatientOptions(res.data?.data || []);
      } catch { /* ignore */ }
    }, patientSearch.length > 0 ? 300 : 50);
    return () => clearTimeout(timer);
  }, [patientSearch, open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: professionalSearch || '' } });
        setProfessionalOptions(res.data?.data || []);
      } catch { /* ignore */ }
    }, professionalSearch.length > 0 ? 300 : 50);
    return () => clearTimeout(timer);
  }, [professionalSearch, open]);

  useEffect(() => {
    if (!open || !initialPatient?.id) return;
    setSelectedPatient(initialPatient);
    setPatientOptions((o) => (o.some((p: any) => p.id === initialPatient.id) ? o : [initialPatient, ...o]));
  }, [open, initialPatient]);

  useEffect(() => {
    if (!open || !initialProfessional?.id) return;
    setSelectedProfessional(initialProfessional);
    setProfessionalOptions((o) => (o.some((p: any) => p.id === initialProfessional.id) ? o : [initialProfessional, ...o]));
  }, [open, initialProfessional]);

  const resetAndClose = () => {
    onClose();
  };

  const applyTemplate = async (template: any) => {
    setPrescriptionForm((prev: any) => ({
      ...prev,
      prescription_type: template.prescription_type || prev.prescription_type,
      diagnosis: template.default_diagnosis || '',
      diagnosis_code: template.default_diagnosis_code || '',
      notes: template.default_notes || '',
    }));
    const tplItems = (template.items || []).map((it: any, idx: number) => ({
      ...emptyItem,
      ...it,
      id: undefined,
      template_id: undefined,
      sort_order: idx,
    }));
    setItems(tplItems.length > 0 ? tplItems : [{ ...emptyItem }]);
  };

  const addItem = () => setItems([...items, { ...emptyItem, sort_order: items.length }]);
  const removeItem = (idx: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx: number, field: string, value: any) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  const handleSave = async () => {
    if (!selectedPatient?.id) return;
    if (!items.some((i) => i.medication_name.trim())) return;

    setSaving(true);
    try {
      const payload = {
        ...prescriptionForm,
        patient_id: selectedPatient.id,
        professional_id: selectedProfessional?.id || null,
        items: items.filter((i) => i.medication_name.trim()).map((item, idx) => ({ ...item, sort_order: idx })),
      };
      const res = await axios.post(getApiUrl('/clinic/prescriptions'), payload);
      let saved = res.data?.data ?? res.data;
      const newId = saved?.id;
      if (newId && !saved?.items?.length) {
        try {
          const full = await axios.get(getApiUrl(`/clinic/prescriptions/${newId}`));
          saved = full.data?.data ?? saved;
        } catch { /* keep partial */ }
      }
      const subtype =
        typeLabels[String(saved?.prescription_type ?? payload.prescription_type)] || 'Receituário';
      const firstMed = items.find((i) => i.medication_name.trim())?.medication_name || '';
      const tail = firstMed ? `${subtype} — ${firstMed}` : subtype;
      const templateName = /^receitu[aá]?rio(\s|$|[—\-–])/i.test(tail)
        ? tail
        : `Receituário — ${tail}`;
      const printable: EvolutionAttachedPrintable = {
        clientKey: crypto.randomUUID(),
        category: 'prescription',
        templateId: prefillTemplate?.id,
        templateName,
        bodyText: summaryFromPrescription(saved, items),
        linkedDocumentId: newId,
        linkedDocumentType: 'prescription',
      };
      onSuccess(printable);
      resetAndClose();
    } catch (err: any) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3, maxHeight: '96vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrescriptionIcon sx={{ color: '#14b8a6' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Novo receituário</Typography>
        </Box>
        <IconButton onClick={resetAndClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Mesmo cadastro da página <strong>Receituários</strong>: após salvar, o documento fica no prontuário e pode ser reimpresso com timbrado da clínica.
        </Alert>

        {templates.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#0284c7' }}>
              Modelos pré-preenchidos
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {templates.slice(0, 10).map((tpl: any) => (
                <Chip
                  key={tpl.id}
                  label={tpl.name}
                  onClick={() => applyTemplate(tpl)}
                  clickable
                  sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1, borderBottom: '1px solid #e5e7eb',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' },
          '& .Mui-selected': { color: '#14b8a6 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#14b8a6' },
        }}>
          <Tab label="Paciente & Profissional" />
          <Tab label="Medicamentos" />
          <Tab label="Informações clínicas" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Tipo de receituário</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={prescriptionForm.prescription_type} label="Tipo"
              onChange={(e) => setPrescriptionForm({ ...prescriptionForm, prescription_type: e.target.value })}>
              {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Paciente *</Typography>
          <Autocomplete
            options={patientOptions}
            getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` - CPF: ${o.cpf}` : ''}`}
            value={selectedPatient}
            onChange={(_, v) => setSelectedPatient(v)}
            onInputChange={(_, v) => setPatientSearch(v)}
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Buscar paciente" fullWidth />}
            noOptionsText="Nenhum paciente encontrado"
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Profissional</Typography>
          <Autocomplete
            options={professionalOptions}
            getOptionLabel={(o: any) => `${o.full_name}${o.registration_number ? ` — ${o.registration_type || 'CRM'} ${o.registration_number}` : ''}`}
            value={selectedProfessional}
            onChange={(_, v) => setSelectedProfessional(v)}
            onInputChange={(_, v) => setProfessionalSearch(v)}
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Buscar profissional" fullWidth />}
            noOptionsText="Nenhum profissional encontrado"
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d9488' }}>Medicamentos</Typography>
            <Button size="small" startIcon={<AddItemIcon />} onClick={addItem} sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Adicionar</Button>
          </Box>
          {items.map((item, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Medicamento {index + 1}</Typography>
                {items.length > 1 && (
                  <IconButton size="small" onClick={() => removeItem(index)} sx={{ color: '#ef4444' }}><RemoveIcon /></IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Nome do medicamento *" value={item.medication_name}
                    onChange={(e) => updateItem(index, 'medication_name', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Princípio ativo" value={item.active_ingredient}
                    onChange={(e) => updateItem(index, 'active_ingredient', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Posologia *" value={item.dosage}
                    onChange={(e) => updateItem(index, 'dosage', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Frequência *" value={item.frequency}
                    onChange={(e) => updateItem(index, 'frequency', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Via</InputLabel>
                    <Select value={item.route} label="Via" onChange={(e) => updateItem(index, 'route', e.target.value)}>
                      {routeOptions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Forma farmacêutica</InputLabel>
                    <Select value={item.pharmaceutical_form} label="Forma farmacêutica"
                      onChange={(e) => updateItem(index, 'pharmaceutical_form', e.target.value)}>
                      {formOptions.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Duração" value={item.duration}
                    onChange={(e) => updateItem(index, 'duration', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Instruções" value={item.instructions} multiline rows={2}
                    onChange={(e) => updateItem(index, 'instructions', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel control={<Switch size="small" checked={item.is_controlled} onChange={(e) => updateItem(index, 'is_controlled', e.target.checked)} color="error" />}
                      label={<Typography variant="caption">Controlado</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={item.is_antimicrobial} onChange={(e) => updateItem(index, 'is_antimicrobial', e.target.checked)} color="warning" />}
                      label={<Typography variant="caption">Antimicrobiano</Typography>} />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Diagnóstico" value={prescriptionForm.diagnosis}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, diagnosis: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="CID-10" value={prescriptionForm.diagnosis_code}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, diagnosis_code: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Indicação clínica" value={prescriptionForm.clinical_indication} multiline rows={2}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, clinical_indication: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observações (receituário)" value={prescriptionForm.notes} multiline rows={3}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, notes: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notas internas" value={prescriptionForm.internal_notes} multiline rows={2}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, internal_notes: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fef3c7' } }} />
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
        <Button onClick={resetAndClose} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !selectedPatient}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600, minWidth: 160 }}>
          {saving ? <CircularProgress size={20} /> : 'Salvar e anexar à evolução'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrescriptionFormDialog;
